package com.wintermar.inspection.core.ui.component.modal.util

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import com.wintermar.inspection.core.ui.component.TextBodyRegular
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.component.button.IconButton
import com.wintermar.inspection.core.ui.component.modal.ModalLoading
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.ic_close

data class ModalContainerConfig(
    val isBottomSheet: Boolean = false,
    val fullScreen: Boolean = false,
    val withBackgroundHeader: Boolean = false,
    val gradientColors: List<Color> = emptyList(),
)

@Composable
fun ModalContainer(
    isLoading: Boolean,
    title: String = "",
    description: String = "",
    config: ModalContainerConfig = ModalContainerConfig(),
    onDismiss: () -> Unit,
    contentHeader: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    val isBottomSheet = config.isBottomSheet
    val fullScreen = config.fullScreen
    val withBackgroundHeader = config.withBackgroundHeader
    val gradientColors = config.gradientColors
    val dimension = LocalAppTheme.current.dimension
    val color = LocalAppTheme.current.color
    val density = LocalDensity.current
    val screenHeightDp = with(density) {
        LocalWindowInfo.current.containerSize.height.toDp()
    }

    Column(
        modifier = Modifier
            .heightIn(
                max = screenHeightDp * 0.85f
            )
            .then(
                if (fullScreen) Modifier.fillMaxSize()
                else Modifier
            )
            .then(
                if (isBottomSheet) Modifier
                else Modifier.background(
                    color = color.surface.normal,
                    shape = RoundedCornerShape(dimension.radius.lg)
                )
            )
            .then(
                if (gradientColors.isEmpty()) Modifier else
                    Modifier
                        .then(
                            if (isBottomSheet) Modifier
                            else Modifier
                                .clip(RoundedCornerShape(dimension.radius.lg))
                        )
                        .drawBehind {
                            val gradientHeight = size.height * 0.4f
                            drawRect(
                                brush = Brush.verticalGradient(
                                    colors = gradientColors,
                                    startY = 0f,
                                    endY = gradientHeight
                                ),
                                size = size
                            )
                        }
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    if (withBackgroundHeader) Modifier.background(
                        color = color.surface.raised,
                        shape = RoundedCornerShape(
                            topStart = dimension.radius.lg,
                            topEnd = dimension.radius.lg
                        )
                    ) else Modifier
                )
                .padding(
                    dimension.spacing.space4,
                ),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(0.8f).padding(
                    horizontal = dimension.spacing.space2
                ),
                verticalArrangement = Arrangement.Center
            ) {
                if (contentHeader != null) contentHeader()
                else {
                    TextBodyRegular(text = title, bold = true)
                    if (description.isNotEmpty()) {
                        TextBodySmall(text = description, color = { it.subtle })
                    }
                }
            }

            IconButton(
                icon = Res.drawable.ic_close,
                backgroundColor = Color.Transparent,
                tint = color.icon.default,
                onClick = onDismiss
            )
        }
        if (withBackgroundHeader) {
            HorizontalDivider(modifier = Modifier.fillMaxWidth(), color = color.border.subtle)
        }
        ModalLoading(isLoading)
        Box(
            modifier = Modifier.then(
                if (fullScreen) Modifier.fillMaxSize()
                else Modifier
            ),
            contentAlignment =
                if (fullScreen) Alignment.TopStart else Alignment.BottomCenter,
        ) {
            content()
        }
    }
}
